import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { GraduationCapIcon, UsersIcon, ShieldCheckIcon, AwardIcon, CheckCircle2Icon, CalendarIcon, BookOpenIcon, MessageCircleIcon, TargetIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Container } from '../../components/ui/Container';
import { Button } from '../../components/ui/Button';
import { SubpageHero } from '../../components/sections/subpages/SubpageHero';
import { PatternDecor } from '../../components/ui/PatternDecor';
const tracks = [{
  icon: GraduationCapIcon,
  title: 'Educator Track',
  body: 'For teachers, tutors, and curriculum developers working with Muslim children and youth in non-Muslim settings.',
  items: ['Child development and safeguarding', 'Identity-aware pedagogy', 'Curriculum design for cultural sensitivity', 'Practical classroom strategies'],
  bg: 'bg-sage-100',
  to: '#teacher-certification'
}, {
  icon: UsersIcon,
  title: 'Community Leadership Track',
  body: 'For mentors, community organizers, and leaders supporting Muslim families and youth at the community level.',
  items: ['Ethical leadership principles', 'Community-based mentorship', 'Safeguarding and accountability', 'Family and youth support frameworks'],
  bg: 'bg-[#F7EDE9]',
  to: '#community-leadership'
}];
const standards = [{
  title: 'Safeguarding First',
  body: 'All programs follow rigorous safeguarding standards. Trust and protection are non-negotiable.'
}, {
  title: 'Ethical Practice',
  body: 'Practitioners operate with transparency, accountability, and respect for family autonomy.'
}, {
  title: 'Certified Completion',
  body: 'Certificates are issued upon successful completion where applicable — recognized within our network.'
}];
export function ProfessionalDevelopment() {
  return <>
      <SubpageHero eyebrow="Certification & leadership for educators and community builders" title="Professional Development" paragraphs={['Professional tracks designed for educators, mentors, and community leaders supporting Muslim families in non-Muslim countries.', 'Practical training, safeguarding standards, and ethical leadership — with certificates issued upon completion where applicable.']} primaryCta={{
      label: 'Explore Programs',
      to: '#programs'
    }} secondaryCta={{
      label: 'Request Info',
      to: '/en/contact'
    }} image="https://cdn.magicpatterns.com/uploads/mZ6s4PwoMdkaamZH9rLkBU/Prof-Dev.jpg" imageAlt="Muslim professional recording an educational discussion at a desk with a laptop and microphone" compact masked data-id="element-2031" data-mp="fhr1i0thmycd" />

      {/* Two professional tracks */}
      <section id="programs" className="py-16 md:py-24 scroll-mt-24 bg-sage-50/60" data-id="element-2032" data-mp="1at72kfwryfui">
        <Container data-id="element-2033" data-mp="1j230awjxafbg">
          <h2 className="font-display text-3xl md:text-5xl text-ink-900 leading-tight" data-id="element-2034" data-mp="1j2mimedhj5q0">
            Two professional tracks
          </h2>
          <p className="mt-5 text-ink-700 leading-relaxed" data-id="element-2035" data-mp="1n595s1z7o2gv">
            Choose the track that matches your role and impact. Both tracks
            emphasize ethical practice, safeguarding, and real-world
            application.
          </p>
          <div className="mt-10 grid md:grid-cols-2 gap-5" data-id="element-2036" data-mp="220yrs04ecfll">
            {tracks.map((t, i) => <motion.div key={t.title} initial={{
            opacity: 0,
            y: 16
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-60px'
          }} transition={{
            duration: 0.5,
            delay: i * 0.08,
            ease: [0.22, 1, 0.36, 1]
          }} whileHover={{
            y: -2
          }} className="rounded-2xl" data-id="element-2037" data-mp="261itlgzx07e2" data-mpi={i}>
                <Link to={t.to} className={`group block h-full rounded-2xl ${t.bg} border border-ink-900/5 p-7 shadow-soft transition-shadow hover:shadow-card`} data-id="element-2038" data-mp="3hghan1oa6hf2">
                  <span className="w-11 h-11 inline-flex items-center justify-center rounded-xl bg-white border border-forest-700/10 text-forest-700" data-id="element-2039" data-mp="33ialc9xedpr0">
                    <t.icon size={18} strokeWidth={1.6} data-id="element-2040" data-mp="2yl0q2dnbujv9" />
                  </span>
                  <h3 className="mt-5 font-display text-xl text-ink-900" data-id="element-2041" data-mp="3cka7fgn9rk0t">
                    {t.title}
                  </h3>
                  <p className="mt-3 text-ink-700 leading-relaxed" data-id="element-2042" data-mp="38gc56x2ndt3j">{t.body}</p>
                  <ul className="mt-5 space-y-2.5" data-id="element-2043" data-mp="rropz1zrwldc">
                    {t.items.map(item => <li key={item} className="flex items-start gap-2.5 text-ink-700 text-sm" data-id="element-2044" data-mp="nqmr4m9rqbbw">
                        <CheckCircle2Icon size={14} className="text-forest-700 mt-0.5 flex-shrink-0" strokeWidth={1.8} data-id="element-2045" data-mp="10ajrx2af0svp" />
                        {item}
                      </li>)}
                  </ul>
                </Link>
              </motion.div>)}
          </div>

          <div className="mt-8 flex flex-col gap-2 rounded-2xl border border-sage-200 bg-white px-6 py-5 sm:flex-row sm:items-center" data-id="element-2046" data-mp="1j11ib07rzcdw">
            <span className="inline-flex w-fit whitespace-nowrap rounded-full bg-sage-100 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-forest-700" data-id="element-2047" data-mp="14b2i6rwntqvs">
              Coming soon
            </span>
            <p className="flex-1 text-center text-sm font-semibold text-ink-700" data-id="element-2048" data-mp="1wy6jalsmtl2a">
              The names of our confirmed teachers, educators, and Shariah Board
              members will be announced soon.
            </p>
          </div>
        </Container>
      </section>

      {/* Teacher Certification */}
      <section id="teacher-certification" className="relative py-16 md:py-24 overflow-hidden isolate scroll-mt-24 bg-white/60" data-id="element-2049" data-mp="26m2dvjlnxfk4">
        <PatternDecor side="right" y="top" size="lg" data-id="element-2050" data-mp="1xm5lb6w5jwjs" />
        <Container data-id="element-2051" data-mp="1so41gjqwjt79">
          <div className="rounded-3xl bg-white border border-sage-200 shadow-soft overflow-hidden" data-id="element-2052" data-mp="1sonlc6044rnx">
            {/* Header Band */}
            <div className="px-7 md:px-10 py-8 bg-sage-50 border-b border-sage-200 flex flex-col md:flex-row md:items-center justify-between gap-6" data-id="element-2053" data-mp="1nvue9bh6978o">
              <div data-id="element-2054" data-mp="1ewz7nya7tv4u">
                <div className="flex items-center gap-3 mb-3" data-id="element-2055" data-mp="1exif2l28p14m">
                  <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-forest-100 text-forest-800 text-xs font-semibold tracking-wide" data-id="element-2056" data-mp="1euwb0vek53r5">
                    12 Weeks
                  </span>
                  <span className="text-ink-500 text-sm font-medium" data-id="element-2057" data-mp="3glvbawry8hxa">
                    Certification
                  </span>
                </div>
                <h2 className="font-display text-3xl md:text-4xl text-ink-900" data-id="element-2058" data-mp="3ljoeri1vaxtv">
                  Teacher Certification
                </h2>
              </div>
              <div className="flex flex-col md:items-end gap-3" data-id="element-2059" data-mp="3uleah02s8ixg">
                <p className="text-2xl font-display text-forest-700" data-id="element-2060" data-mp="3ujjve2e6zl1a">
                  €590{' '}
                  <span className="text-base text-ink-500 font-sans" data-id="element-2061" data-mp="2xtanvuv0r50v">
                    / certification
                  </span>
                </p>
                <Link to="/waitlist" data-id="element-2062" data-mp="36vtkvyc4inf7">
                  <Button variant="primary" data-id="element-2063" data-mp="36usc60ikyd0n">Reserve your seat</Button>
                </Link>
              </div>
            </div>

            <div className="grid lg:grid-cols-12 gap-0" data-id="element-2064" data-mp="2f4vvb351zixy">
              {/* Left Column: Content */}
              <div className="lg:col-span-7 px-7 md:px-10 py-10 border-b lg:border-b-0 lg:border-r border-sage-200" data-id="element-2065" data-mp="2j6hdn40cwpfj">
                {/* Outcome-first framing */}
                <h3 className="text-xl md:text-2xl font-display text-ink-900 leading-snug" data-id="element-2066" data-mp="1cjf4b1o1ei0t">
                  Become a confident, certified educator — equipped to teach
                  Muslim children with care, safeguarding, and ethical,
                  identity-aware methods.
                </h3>
                <p className="mt-4 text-ink-700 leading-relaxed" data-id="element-2067" data-mp="1hgyjc3sk6ytt">
                  A structured online certification for educators supporting
                  Muslim children in non-Muslim countries, grounded in child
                  development, ethical teaching standards, and safeguarding.
                </p>

                {/* Who it's for (Chips) */}
                <div className="mt-10" data-id="element-2068" data-mp="209sknux9esfk">
                  <h4 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-forest-700 mb-4" data-id="element-2069" data-mp="20au86yvgp7e6">
                    Who this is for
                  </h4>
                  <div className="flex flex-wrap gap-2" data-id="element-2070" data-mp="2a88c5z0783pl">
                    {['Teachers and educators', 'Homeschool parents', 'Youth mentors', 'Community workers'].map(chip => <span key={chip} className="inline-flex items-center px-3 py-1.5 rounded-lg bg-sand-50 border border-sand-200 text-ink-700 text-sm" data-id="element-2071" data-mp="2jtrgzxh8cjjt">
                        {chip}
                      </span>)}
                  </div>
                </div>

                {/* What's included (Icon row) */}
                <div className="mt-10" data-id="element-2072" data-mp="215cp77z391hy">
                  <h4 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-forest-700 mb-4" data-id="element-2073" data-mp="2128fjv2utdjw">
                    What's included
                  </h4>
                  <div className="grid sm:grid-cols-2 gap-4" data-id="element-2074" data-mp="1r6druljrwsa9">
                    {[{
                    icon: BookOpenIcon,
                    text: 'Structured online delivery'
                  }, {
                    icon: CheckCircle2Icon,
                    text: 'Ethical teaching standards'
                  }, {
                    icon: ShieldCheckIcon,
                    text: 'Safeguarding & responsibility'
                  }, {
                    icon: AwardIcon,
                    text: 'Certificate of completion'
                  }].map(f => <div key={f.text} className="flex items-center gap-3" data-id="element-2075" data-mp="28gn8zezm0kc7">
                        <span className="w-8 h-8 rounded-full bg-sage-100 text-forest-700 flex items-center justify-center flex-shrink-0" data-id="element-2076" data-mp="28ej0skuogz64">
                          <f.icon size={14} strokeWidth={2} data-id="element-2077" data-mp="314sui3yhmdd3" />
                        </span>
                        <span className="text-sm text-ink-700" data-id="element-2078" data-mp="35wj6sfgzjb75">{f.text}</span>
                      </div>)}
                  </div>
                </div>

                {/* Outcomes */}
                <div className="mt-10 bg-sage-50 rounded-2xl p-6 border border-sage-200" data-id="element-2079" data-mp="pih1znguso46">
                  <h4 className="font-display text-lg text-ink-900 mb-4" data-id="element-2080" data-mp="139voi49yz7fz">
                    Where you'll be in 12 weeks
                  </h4>
                  <ul className="space-y-3" data-id="element-2081" data-mp="z8afiu0k3db2">
                    {['A certification recognized within our partner network', 'Confidence in safeguarding & ethics', 'Identity-aware teaching methods', 'Practical online classroom skills'].map(item => <li key={item} className="flex items-start gap-3 text-ink-800 font-medium" data-id="element-2082" data-mp="fji5f39d4ixj">
                        <CheckCircle2Icon size={18} className="text-forest-700 mt-0.5 flex-shrink-0" strokeWidth={2} data-id="element-2083" data-mp="1qyyh27656kp9" />
                        {item}
                      </li>)}
                  </ul>
                </div>
              </div>

              {/* Right Column: Curriculum & Pricing */}
              <div className="lg:col-span-5 bg-[#FAFAFA]" data-id="element-2084" data-mp="12ltfukn3wf0b">
                <div className="px-7 md:px-10 py-10" data-id="element-2085" data-mp="12j78om44o1im">
                  <h4 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-forest-700 mb-6" data-id="element-2086" data-mp="5w2bltp0jdhb">
                    Course Curriculum
                  </h4>

                  {/* Curriculum list */}
                  <div className="relative border-l border-sage-200 ml-3 space-y-8 pb-4" data-id="element-2087" data-mp="euxjrbbmaxn9">
                    {[{
                    num: '1',
                    title: 'Educational Psychology & Child Development',
                    desc: 'Cognitive, emotional, and social development stages in Muslim children'
                  }, {
                    num: '2',
                    title: 'Foundations of Tarbiyah & Ethics',
                    desc: "Islamic educational philosophy, prophetic methodology, and teacher's adab"
                  }, {
                    num: '3',
                    title: 'Modern Pedagogy & Teaching Methodologies',
                    desc: 'Active learning, inquiry-based instruction, and critical thinking tools'
                  }, {
                    num: '4',
                    title: 'Inclusive Education & Diverse Learning Needs',
                    desc: 'Identifying learning barriers, differentiation strategies, and supportive adjustments'
                  }, {
                    num: '5',
                    title: 'Curriculum Mapping & Syllabus Design',
                    desc: 'Aligning educational standards with core values and structured learning paths'
                  }, {
                    num: '6',
                    title: 'Lesson Planning & Resource Development',
                    desc: 'Creating actionable, engaging lesson plans and selecting ethical materials'
                  }, {
                    num: '7',
                    title: 'Classroom Management & Positive Discipline',
                    desc: 'Fostering a respectful learning culture, behavior intervention, and motivation'
                  }, {
                    num: '8',
                    title: 'Safeguarding, Child Protection & Legal Duty',
                    desc: 'Child safety regulations, identifying risks, compliance, and institutional policy'
                  }, {
                    num: '9',
                    title: 'Educational Technology & Digital Literacy',
                    desc: 'Utilizing modern digital tools safely and effectively in the learning process'
                  }, {
                    num: '10',
                    title: 'Student Assessment & Progress Tracking',
                    desc: 'Formative and summative evaluation, grading criteria, and constructive feedback'
                  }, {
                    num: '11',
                    title: 'Parent-Teacher Collaboration & Communication',
                    desc: 'Building trust with families, conducting conferences, and reporting progress'
                  }, {
                    num: '12',
                    title: 'Professional Practicum & Portfolio Defense',
                    desc: 'Final teaching demonstration, portfolio review, and certification award'
                  }].map(subject => <div key={subject.num} className="relative pl-6" data-id="element-2088" data-mp="2m9wm6yne766c">
                        <span className="absolute -left-[13px] top-0 w-6 h-6 rounded-full bg-white border-2 border-forest-700 flex items-center justify-center text-[10px] font-bold text-forest-700" data-id="element-2089" data-mp="2r87rcw2qssi8">
                          {subject.num}
                        </span>
                        <h5 className="font-display text-lg text-ink-900 leading-none mb-2 pt-1" data-id="element-2090" data-mp="314avg7h5n17t">
                          {subject.title}
                        </h5>
                        <p className="text-sm text-ink-600" data-id="element-2091" data-mp="3a5r70ijph9dm">{subject.desc}</p>
                      </div>)}
                  </div>

                  {/* Pricing Module */}
                  <div className="mt-10 rounded-2xl bg-white border border-sage-200 p-6 shadow-sm" data-id="element-2092" data-mp="3jvtw47npi36o">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-500 mb-2" data-id="element-2093" data-mp="3owzqe9x0lyib">
                      Tuition
                    </p>
                    <div className="flex items-baseline gap-2 mb-4" data-id="element-2094" data-mp="1ha80yzazecdl">
                      <span className="font-display text-3xl text-ink-900" data-id="element-2095" data-mp="1hbj4qz4ffk4p">
                        €590
                      </span>
                      <span className="text-ink-500 text-sm" data-id="element-2096" data-mp="1899w6espy0ty">
                        / certification
                      </span>
                    </div>

                    <div className="space-y-3 mb-6" data-id="element-2097" data-mp="13c9s29ffj85h">
                      <div className="flex items-center gap-2 text-sm text-ink-700" data-id="element-2098" data-mp="20yp7l5oapvqe">
                        <AwardIcon size={14} className="text-forest-700" data-id="element-2099" data-mp="1vzcomw0jp8t3" />
                        <span data-id="element-2100" data-mp="1w1gon2vbpno3">Certificate upon completion</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-ink-700" data-id="element-2101" data-mp="1r6b5gbtb2n3p">
                        <UsersIcon size={14} className="text-forest-700" data-id="element-2102" data-mp="1m8rov594xy8l" />
                        <span data-id="element-2103" data-mp="2jpqfwkbb33mm">Small, dedicated cohorts</span>
                      </div>
                      <div className="flex items-start gap-2 text-sm text-ink-700" data-id="element-2104" data-mp="2exovuc7wc8m1">
                        <ShieldCheckIcon size={14} className="text-forest-700 mt-0.5 flex-shrink-0" data-id="element-2105" data-mp="1n9vnhbk7acks" />
                        <span data-id="element-2106" data-mp="1s2yh1joz9m0z">
                          Limited scholarships via community partnerships
                        </span>
                      </div>
                    </div>

                    <Link to="/waitlist" className="block w-full" data-id="element-2107" data-mp="1i97uwlgbpnog">
                      <Button variant="primary" className="w-full justify-center" data-id="element-2108" data-mp="2626iykciz88v">
                        Reserve your seat
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* Community Leadership */}
      <section id="community-leadership" className="py-16 md:py-24 bg-sand-50/60 scroll-mt-24" data-id="element-2109" data-mp="ljqk5s2hhbfw">
        <Container data-id="element-2110" data-mp="qhs56ijgzku0">
          <div className="rounded-3xl bg-white border border-sage-200 shadow-soft overflow-hidden" data-id="element-2111" data-mp="i2kzvq9kkful">
            {/* Header Band */}
            <div className="px-7 md:px-10 py-8 bg-sage-50 border-b border-sage-200 flex flex-col md:flex-row md:items-center justify-between gap-6" data-id="element-2112" data-mp="e0r33t2hvvzp">
              <div data-id="element-2113" data-mp="n27bxwdnvq1t">
                <div className="flex items-center gap-3 mb-3" data-id="element-2114" data-mp="n1pamlc56ln1">
                  <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-forest-100 text-forest-800 text-xs font-semibold tracking-wide" data-id="element-2115" data-mp="n0e5ah31t5tt">
                    10 Weeks
                  </span>
                  <span className="text-ink-500 text-sm font-medium" data-id="element-2116" data-mp="92frpdwjxdfd">
                    Certification
                  </span>
                </div>
                <h2 className="font-display text-3xl md:text-4xl text-ink-900" data-id="element-2117" data-mp="4axpynmpf8tc">
                  Community Leadership
                </h2>
              </div>
              <div className="flex flex-col md:items-end gap-3" data-id="element-2118" data-mp="37spokkvcir0i">
                <p className="text-2xl font-display text-forest-700" data-id="element-2119" data-mp="37s68zat64epn">
                  €490{' '}
                  <span className="text-base text-ink-500 font-sans" data-id="element-2120" data-mp="248jjmbgu1v1w">
                    / certification
                  </span>
                </p>
                <Link to="/waitlist" data-id="element-2121" data-mp="2dyl1ummi8j8w">
                  <Button variant="primary" data-id="element-2122" data-mp="2dzmjxb1xu604">Reserve your seat</Button>
                </Link>
              </div>
            </div>

            <div className="grid lg:grid-cols-12 gap-0" data-id="element-2123" data-mp="295ibsogxrrxg">
              {/* Left Column: Content */}
              <div className="lg:col-span-7 px-7 md:px-10 py-10 border-b lg:border-b-0 lg:border-r border-sage-200" data-id="element-2124" data-mp="2hzol7j3rgxev">
                {/* Outcome-first framing */}
                <h3 className="text-xl md:text-2xl font-display text-ink-900 leading-snug" data-id="element-2125" data-mp="2wwfct67714tx">
                  Lead your community with confidence — equipped with ethical
                  frameworks, communication skills, and a real, actionable
                  project plan.
                </h3>
                <p className="mt-4 text-ink-700 leading-relaxed" data-id="element-2126" data-mp="xle8abiphh39">
                  A certification for community leaders and coordinators,
                  blending Islamic ethical leadership with practical
                  communication, conflict resolution, and community project
                  design.
                </p>

                {/* Who it's for (Chips) */}
                <div className="mt-10" data-id="element-2127" data-mp="34fet32zcyg92">
                  <h4 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-forest-700 mb-4" data-id="element-2128" data-mp="34edewpn1pdn4">
                    Who this is for
                  </h4>
                  <div className="flex flex-wrap gap-2" data-id="element-2129" data-mp="2uj1ze53tc2eb">
                    {['Community leaders & coordinators', 'Imams & assistants (community role)', "Youth & women's group coordinators", 'Active volunteers & mentors'].map(chip => <span key={chip} className="inline-flex items-center px-3 py-1.5 rounded-lg bg-sand-50 border border-sand-200 text-ink-700 text-sm" data-id="element-2130" data-mp="jq7mym2i7156">
                        {chip}
                      </span>)}
                  </div>
                </div>

                {/* What's included (Icon row) */}
                <div className="mt-10" data-id="element-2131" data-mp="3c1emz4wt1lx">
                  <h4 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-forest-700 mb-4" data-id="element-2132" data-mp="3d2z1u7tf0gb">
                    What's included
                  </h4>
                  <div className="grid sm:grid-cols-2 gap-4" data-id="element-2133" data-mp="vzx9dyax4jg2">
                    {[{
                    icon: TargetIcon,
                    text: 'Leadership frameworks with Islamic ethics'
                  }, {
                    icon: MessageCircleIcon,
                    text: 'Communication & conflict tools'
                  }, {
                    icon: BookOpenIcon,
                    text: 'A practical community project plan'
                  }, {
                    icon: AwardIcon,
                    text: 'Certificate of completion'
                  }].map(f => <div key={f.text} className="flex items-center gap-3" data-id="element-2134" data-mp="1waq2rdnui086">
                        <span className="w-8 h-8 rounded-full bg-sage-100 text-forest-700 flex items-center justify-center flex-shrink-0" data-id="element-2135" data-mp="219b63lye900u">
                          <f.icon size={14} strokeWidth={2} data-id="element-2136" data-mp="2a8eo0mltr9dr" />
                        </span>
                        <span className="text-sm text-ink-700" data-id="element-2137" data-mp="2ez4w47lmxqc5">{f.text}</span>
                      </div>)}
                  </div>
                </div>

                {/* Outcomes */}
                <div className="mt-10 bg-sage-50 rounded-2xl p-6 border border-sage-200" data-id="element-2138" data-mp="2oxaz6sj6h2il">
                  <h4 className="font-display text-lg text-ink-900 mb-4" data-id="element-2139" data-mp="2ovhql7p0887y">
                    Where you'll be in 10 weeks
                  </h4>
                  <ul className="space-y-3" data-id="element-2140" data-mp="3qmr14cmv586k">
                    {['An ethical leadership toolkit', 'Stronger communication & conflict skills', 'A ready-to-run community project', 'A certification recognized within our partner network'].map(item => <li key={item} className="flex items-start gap-3 text-ink-800 font-medium" data-id="element-2141" data-mp="37oogsb59fzf3">
                        <CheckCircle2Icon size={18} className="text-forest-700 mt-0.5 flex-shrink-0" strokeWidth={2} data-id="element-2142" data-mp="32ry3twor5jxw" />
                        {item}
                      </li>)}
                  </ul>
                </div>
              </div>

              {/* Right Column: Curriculum & Pricing */}
              <div className="lg:col-span-5 bg-[#FAFAFA]" data-id="element-2143" data-mp="zhb9crumli9x">
                <div className="px-7 md:px-10 py-10" data-id="element-2144" data-mp="zfqet28b9zc2">
                  <h4 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-forest-700 mb-6" data-id="element-2145" data-mp="14gxlexypwsh5">
                    Course Curriculum
                  </h4>

                  {/* Curriculum list */}
                  <div className="relative border-l border-sage-200 ml-3 space-y-8 pb-4" data-id="element-2146" data-mp="uhf5cslxhqud">
                    {[{
                    num: '1',
                    title: 'Prophetic Leadership & Servant Model',
                    desc: 'Ethical frameworks, accountability, and the concept of Amanah (trust)'
                  }, {
                    num: '2',
                    title: 'Strategic Community Needs Assessment',
                    desc: 'Demographic analysis, mapping local infrastructure, and identifying systemic gaps'
                  }, {
                    num: '3',
                    title: 'Advanced Communication & Public Relations',
                    desc: 'Public speaking, media engagement, crisis communication, and cross-cultural dialogue'
                  }, {
                    num: '4',
                    title: 'Project Design & Operational Planning',
                    desc: 'Transforming community needs into structured, executable, and measurable project plans'
                  }, {
                    num: '5',
                    title: 'Safeguarding, Ethics & Volunteer Management',
                    desc: 'Ensuring boundaries, safety protocols, and effective coordination of community teams'
                  }, {
                    num: '6',
                    title: 'Financial Stewardship & Ethical Fundraising',
                    desc: 'Grant writing, crowdfunding, halal budgeting, and transparent resource allocation'
                  }, {
                    num: '7',
                    title: 'Conflict Resolution & Community Mediation',
                    desc: 'Navigating internal friction, balancing diverse stakeholders, and consensus-building'
                  }, {
                    num: '8',
                    title: 'Legal Compliance & Western Regulations',
                    desc: 'Governance structures, NGO/charity laws, compliance, and risk management'
                  }, {
                    num: '9',
                    title: 'Strategic Marketing & Community Mobilization',
                    desc: 'Building awareness, digital outreach, and driving sustainable public engagement'
                  }, {
                    num: '10',
                    title: 'Project Launch & Long-Term Sustainability',
                    desc: 'Executing the capstone project, impact evaluation, and strategic scaling'
                  }].map(subject => <div key={subject.num} className="relative pl-6" data-id="element-2147" data-mp="1qnqi59r2vu9l">
                        <span className="absolute -left-[13px] top-0 w-6 h-6 rounded-full bg-white border-2 border-forest-700 flex items-center justify-center text-[10px] font-bold text-forest-700" data-id="element-2148" data-mp="1qlnkc3joowub">
                          {subject.num}
                        </span>
                        <h5 className="font-display text-lg text-ink-900 leading-none mb-2 pt-1" data-id="element-2149" data-mp="1uok808o2zj54">
                          {subject.title}
                        </h5>
                        <p className="text-sm text-ink-600" data-id="element-2150" data-mp="24elmq9ujnp7d">{subject.desc}</p>
                      </div>)}
                  </div>

                  {/* Pricing Module */}
                  <div className="mt-10 rounded-2xl bg-white border border-sage-200 p-6 shadow-sm" data-id="element-2151" data-mp="2edkxpz2s7wxj">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-500 mb-2" data-id="element-2152" data-mp="2x1v5ta2jc28b">
                      Tuition
                    </p>
                    <div className="flex items-baseline gap-2 mb-4" data-id="element-2153" data-mp="31yltojkl024a">
                      <span className="font-display text-3xl text-ink-900" data-id="element-2154" data-mp="2o05b9vdcwn6b">
                        €490
                      </span>
                      <span className="text-ink-500 text-sm" data-id="element-2155" data-mp="2t24ft8ku1q4u">
                        / certification
                      </span>
                    </div>

                    <div className="space-y-3 mb-6" data-id="element-2156" data-mp="2j2voknhcqpm6">
                      <div className="flex items-center gap-2 text-sm text-ink-700" data-id="element-2157" data-mp="25afitex2gdhk">
                        <AwardIcon size={14} className="text-forest-700" data-id="element-2158" data-mp="258cle9tl3i2t" />
                        <span data-id="element-2159" data-mp="29ck8y3ob2rzg">Certificate upon completion</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-ink-700" data-id="element-2160" data-mp="1vdkjitsyy928">
                        <TargetIcon size={14} className="text-forest-700" data-id="element-2161" data-mp="20aazlh4rx4nn" />
                        <span data-id="element-2162" data-mp="1xnox6j3n1wbi">Real community project plan</span>
                      </div>
                      <div className="flex items-start gap-2 text-sm text-ink-700" data-id="element-2163" data-mp="22m8ichaer42t">
                        <UsersIcon size={14} className="text-forest-700 mt-0.5 flex-shrink-0" data-id="element-2164" data-mp="22ocu1lejt7dn" />
                        <span data-id="element-2165" data-mp="2bohrp3txwrem">
                          Limited scholarships via community partnerships
                        </span>
                      </div>
                    </div>

                    <Link to="/waitlist" className="block w-full" data-id="element-2166" data-mp="2ljt7zqkzy6oh">
                      <Button variant="primary" className="w-full justify-center" data-id="element-2167" data-mp="2qbkrxi9nde5n">
                        Reserve your seat
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* Standards */}
      <section className="relative py-16 md:py-24 overflow-hidden isolate bg-white/60" data-id="element-2168" data-mp="3eg2e46m5uuwb">
        <PatternDecor side="left" y="middle" size="md" data-id="element-2169" data-mp="39g6aw9laf7ht" />
        <Container data-id="element-2170" data-mp="39isk0dnttv25">
          <h2 className="font-display text-3xl md:text-5xl text-ink-900 leading-tight" data-id="element-2171" data-mp="34hmpqbeipzqi">
            Standards & certification
          </h2>
          <p className="mt-5 text-ink-700 leading-relaxed" data-id="element-2172" data-mp="30h2oowqtqbz3">
            Our professional programs are built around three commitments:
            safeguarding, ethical practice, and recognized completion.
          </p>
          <div className="mt-10 grid md:grid-cols-3 gap-5" data-id="element-2173" data-mp="3s7qoa4cun9an">
            {standards.map((s, i) => {
            const tones = ['bg-sage-100', 'bg-[#EDF2F7]', 'bg-[#F7EDE9]'];
            return <motion.div key={s.title} initial={{
              opacity: 0,
              y: 16
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true,
              margin: '-60px'
            }} transition={{
              duration: 0.5,
              delay: i * 0.06
            }} className={`rounded-2xl ${tones[i % tones.length]} border border-ink-900/5 p-6 shadow-soft`} data-id="element-2174" data-mp="3jen5dgjfq48m" data-mpi={i}>
                  <span className="w-11 h-11 inline-flex items-center justify-center rounded-xl bg-white text-forest-700" data-id="element-2175" data-mp="1qhrx97x05271">
                    {i === 0 ? <ShieldCheckIcon size={18} strokeWidth={1.6} data-id="element-2176" data-mp="1verr8kgkuehs" /> : i === 1 ? <CheckCircle2Icon size={18} strokeWidth={1.6} data-id="element-2177" data-mp="1gm842t9tohlz" /> : <AwardIcon size={18} strokeWidth={1.6} data-id="element-2178" data-mp="1lkrqeusbvvev" />}
                  </span>
                  <h3 className="mt-5 font-display text-lg text-ink-900" data-id="element-2179" data-mp="3fwzf3qe23g27">
                    {s.title}
                  </h3>
                  <p className="mt-2 text-ink-700 text-sm leading-relaxed" data-id="element-2180" data-mp="148ulea4kvyso">
                    {s.body}
                  </p>
                </motion.div>;
          })}
          </div>
        </Container>
      </section>

      {/* Admission & Scholarships */}
      <section className="py-16 md:py-24 bg-[#EDF2F7]/60" data-id="element-2181" data-mp="198pbwckfymd7">
        <Container data-id="element-2182" data-mp="1974iwrdrzbhg">
          <h3 className="font-display text-lg text-ink-900" data-id="element-2183" data-mp="196mcx31b9iwc">
            Admission & Scholarships
          </h3>
          <p className="mt-4 text-ink-700 leading-relaxed" data-id="element-2184" data-mp="1iyinhdkl31hg">
            Some cohorts may include limited scholarship places through
            community partnerships. Certification tracks may include completion
            requirements and assessment.
          </p>
        </Container>
      </section>

      {/* CTA */}
      <section className="relative py-16 md:py-24 overflow-hidden isolate bg-sage-50/60" data-id="element-2185" data-mp="2jqt4v3vnk5i">
        <PatternDecor side="right" y="bottom" size="md" data-id="element-2186" data-mp="3tqtjjkbw5meo" />
        <Container data-id="element-2187" data-mp="3tsegfglohhfp">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8" data-id="element-2188" data-mp="3tswmf4y57a0t">
            <div className="max-w-xl" data-id="element-2189" data-mp="3v9tnspty9x78">
              <h2 className="font-display text-3xl md:text-5xl text-ink-900 leading-tight" data-id="element-2190" data-mp="40wfyw9vikyq">
                Level up your impact
              </h2>
              <p className="mt-4 text-ink-700" data-id="element-2191" data-mp="91ak438ctarh">
                Request details and choose the right professional track for your
                role.
              </p>
            </div>
            <div className="flex flex-wrap gap-3" data-id="element-2192" data-mp="3cisq054kg7p9">
              <Link to="/en/waitlist" data-id="element-2193" data-mp="3hg2ixur5z1hu">
                <Button variant="primary" data-id="element-2194" data-mp="3hh3v62jx1u15">Join</Button>
              </Link>
              <Link to="/en/contact" data-id="element-2195" data-mp="3mf5e8nhcdtcm">
                <Button variant="secondary" data-id="element-2196" data-mp="3me45ipnstiy2">Request Info</Button>
              </Link>
            </div>
          </div>
        </Container>
      </section>
    </>;
}